import React, { useRef, useState, useCallback, useContext, useDebugValue } from 'react';
import { useLayoutEffectWithoutSSRComplaining } from '@wix/da-hooks/pkg/useLayoutEffectWithoutSSRComplaining';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import { DropdownDebugContext } from '../DropdownStateManager/DropdownStateContext';
export function useDropdownState(menuRef, toggleRef, { disabled, openOnHover, closeOnLeave = true, openBiData, onOpen, closeBiData, onClose, onToggle }) {
    const logBiEvent = useBiLogger();
    const { forceOpen } = useContext(DropdownDebugContext);
    const [isOpen, setIsOpen] = useState(forceOpen);
    const wasOpen = useRef(false);
    const focusWasInDropdown = useRef(false);
    const openDropdown = useCallback(()=>{
        if (isOpen || disabled) {
            return;
        }
        wasOpen.current = isOpen;
        focusWasInDropdown.current = false;
        setIsOpen(true);
        onOpen && onOpen();
        onToggle && onToggle(true);
        openBiData && logBiEvent(openBiData);
    }, [
        disabled,
        isOpen,
        onOpen,
        onToggle,
        logBiEvent,
        openBiData
    ]);
    const closeDropdown = useCallback(()=>{
        if (!isOpen || disabled || forceOpen) {
            return;
        }
        wasOpen.current = isOpen;
        focusWasInDropdown.current = !!menuRef.current && menuRef.current.contains(document.activeElement);
        setIsOpen(false);
        onClose && onClose();
        onToggle && onToggle(false);
        closeBiData && logBiEvent(closeBiData);
    }, [
        isOpen,
        disabled,
        forceOpen,
        menuRef,
        onClose,
        onToggle,
        closeBiData,
        logBiEvent
    ]);
    const toggleDropdown = useCallback(()=>{
        if (isOpen) {
            closeDropdown();
        } else {
            openDropdown();
        }
    }, [
        closeDropdown,
        isOpen,
        openDropdown
    ]);
    // set focus after state change
    useLayoutEffectWithoutSSRComplaining(()=>{
        if (isOpen && !wasOpen.current) {
            // focus menu
            if (menuRef.current) {
                menuRef.current.focus({
                    preventScroll: true
                });
            }
        }
        if (!isOpen && wasOpen.current) {
            // focus toggle
            if (focusWasInDropdown.current && toggleRef.current) {
                toggleRef.current.focus({
                    preventScroll: true
                });
            }
        }
    }, [
        isOpen
    ]);
    useDebugValue(isOpen ? 'Open' : 'Closed');
    return {
        isOpen,
        openOnHover,
        closeOnLeave,
        openDropdown,
        closeDropdown,
        toggleDropdown
    };
}
